import type { ChangeEvent } from 'react';

import type { Locale } from 'date-fns';
import type { DateRange, DayPickerProps, Matcher } from 'react-day-picker';

import {
  setHours,
  setMinutes,
  differenceInDays,
  differenceInMonths,
  format,
  isPast,
  isFuture,
  getHours,
  getMinutes,
} from 'date-fns';
import { enUS } from 'date-fns/locale/en-US';
import React, { useEffect, useRef, useState } from 'react';
import { DayPicker } from 'react-day-picker';
import { useTranslation } from 'react-i18next';

import { CalendarIcon, CloseIcon } from '../../icons';
import { ChevronIcon } from '../../icons/chevron';
import { classNames } from '../../utils/class-names';
import { IconButton, PrimaryButton } from '../buttons';
import { InputDescription } from '../input-description';

import styles from './date-picker.module.css';
import { TextInput } from '../text-input/text-input';
import { Dropdown } from '../dropdown';

export type DatePickerProps = {
  className?: string;
  selectClassName?: string;
  label?: string;
  value?: Date | DateRange | null;
  today?: Date;
  asDropdown?: boolean;
  dropdownLabelDateFormat?: string;
  disablePastDays?: boolean;
  disableFutureDays?: boolean;
  locale?: Locale;
  showCalendarIcon?: boolean;
  startMonth?: DayPickerProps['startMonth'];
  endMonth?: DayPickerProps['endMonth'];
  captionLayout?: DayPickerProps['captionLayout'];
  onChange?: (value: Date | DateRange | undefined | null) => void;
  id?: string;
  disabled?: boolean;
  mode?: 'single' | 'range';
  numberOfMonths?: number;
  min?: number;
  submitLabel?: string;
  isSubmitNeeded?: boolean;
  isClearable?: boolean;
  hasTime?: boolean;
  datepickerProps?: Omit<DayPickerProps, 'mode' | 'required' | 'selected' | 'onSelect' | 'onMonthChange'>;
};

/**
 * @param {string} [className] An optional className to set to the date-picker.
 * @default undefined
 * @param {string} [selectClassName] An optional selectClassName to set to the date-picker select.
 * @default undefined
 * @param {string} [label] An optional label to set as a placeholder for the select.
 * @default undefined
 * @param {string} [id] An optional id to set to the date-picker.
 * @default undefined
 * @param {Date} [value] The date that is currently selected
 * @default undefined
 * @param {Date} [today] The current date
 * @default new Date()
 * @param {boolean} [asDropdown] Whether to render date picker as a dropdown element
 * @default true
 * @param {string} [dropdownLabelDateFormat] The date format to use for the dropdown label (https://date-fns.org/v2.28.0/docs/format)
 * @default 'PPPP'
 * @param {boolean} [disablePastDays] Whether to disable past days from being selected
 * @default true
 * @param {boolean} [disableFutureDays] Whether to disable future days from being selected
 * @default false
 * @param {Locale} [locale] The locale to use for the date picker
 * @default en
 * @param {boolean} [showCalendarIcon] Whether to show the calendar icon
 * @default undefined
 * @param {Date} [startMonth] The first month to show in the datepicker
 * @default undefined
 * @param {Date} [endMonth] The last month to show in the datepicker
 * @default undefined
 * @param {DayPickerBase['captionLayout']} [captionLayout] The layout of the caption (if you want to use "dropdown" value, you need to also set fromYear and toYear props)
 * @default undefined
 * @param {Function} [onChange] A callback that is called when the chosen date is changed
 * @default undefined
 * @param {boolean} [disabled] Whether the date picker is disabled
 * @default 'single'
 * @param {'single' | 'range'} [mode] Datepicker mode type
 * @default undefined
 * @param {number} [numberOfMonths] Number of months to show in the date picker
 * @default 1
 * @param {number} [min] Minimum number of days to select in range mode
 * @default undefined
 * @param {object} [datepickerProps] Additional props to pass to the DayPicker component
 * @default undefined
 * @example
 * * Disables today, dates after {maxDate} and before {minDate}
 * <DatePicker
 *   label="Select a date"
 *   value={new Date()}
 *   onChange={(value) => console.log(value)}
 *   datepickerProps={{
 *    disabled: [ today, { after: new Date({maxDate}) }, before: new Date({minDate}) ],
 *   }}
 * />
 */

function formatValue(
  value: Date | DateRange | undefined,
  dropdownLabelDateFormat: string,
  locale: Locale,
  isRangeMode: boolean
): string {
  if (value === undefined) return '';

  if (isRangeMode) {
    const { from, to } = value as DateRange;

    return `${format(from || '', dropdownLabelDateFormat, { locale })}-${to ? format(to, dropdownLabelDateFormat, { locale }) : ''}`;
  }

  return format(value as Date, dropdownLabelDateFormat, { locale });
}

function getTimeFromDate(date: Date): string {
  if (!date) return '00:00';

  const hours = getHours(date);
  const minutes = getMinutes(date);
  const formattedHours = String(hours).padStart(2, '0');
  const formattedMinutes = String(minutes).padStart(2, '0');
  return `${formattedHours}:${formattedMinutes}`;
}

export function DatePicker({
  className,
  selectClassName,
  id,
  label,
  value,
  today = new Date(),
  asDropdown = true,
  dropdownLabelDateFormat = 'PPPP',
  disablePastDays = true,
  disableFutureDays = false,
  locale = enUS,
  showCalendarIcon,
  startMonth,
  endMonth,
  captionLayout = 'label',
  disabled,
  mode = 'single',
  numberOfMonths,
  min = 1,
  submitLabel,
  isSubmitNeeded,
  isClearable,
  hasTime,
  onChange,
  datepickerProps,
}: DatePickerProps) {
  const { t } = useTranslation();
  const containerRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [dateValue, setDateValue] = useState(value);
  const [submitValue, setSubmitValue] = useState(value);
  const [timeValue, setTimeValue] = useState<string>(getTimeFromDate(value as Date));
  const [timeError, setTimeError] = useState<string>();
  const isRangeMode: boolean = mode === 'range';
  const [month, setMonth] = useState(value && 'from' in value ? value.from : value || undefined);
  const defaultIsSubmitNeeded = hasTime ? true : isSubmitNeeded;
  const datepickerContainerClassName = classNames(
    styles.datePickerContainer,
    className,
    asDropdown && styles.isDropdown
  );

  const dropdownLabel = value
    ? formatValue(value, dropdownLabelDateFormat, locale, isRangeMode)
    : disabled
      ? ''
      : label || t('datePicker.dropdown.placeholder');

  function validateTime(dateTimeValue: Date) {
    if (!((disablePastDays || disableFutureDays) && dateTimeValue && timeValue)) return;

    setTimeError(undefined);

    if (disablePastDays && isPast(dateTimeValue as Date)) {
      setTimeError(t('datePicker.time.pastError'));
    } else if (disableFutureDays && isFuture(dateTimeValue as Date)) {
      setTimeError(t('datePicker.time.futureError'));
    }
  }

  const handleSelect = (selected: Date | DateRange | undefined | null, isSubmitting = false) => {
    let newSelected = selected;

    // Datepicker with time picker
    if (newSelected && newSelected instanceof Date && hasTime) {
      const [hours, minutes] = timeValue.split(':').map((str) => parseInt(str, 10));
      newSelected = setHours(setMinutes(newSelected, minutes), hours);
    }

    if (
      disablePastDays &&
      newSelected !== undefined &&
      newSelected !== null &&
      differenceInDays(newSelected as Date, today) < 0
    ) {
      return;
    } else if (defaultIsSubmitNeeded && !isSubmitting) {
      setDateValue(newSelected);
      validateTime(newSelected as Date);
      return;
    }

    setSubmitValue(newSelected);
    onChange?.(newSelected);

    if (!defaultIsSubmitNeeded) setDateValue(newSelected);

    if (isRangeMode && (newSelected as DateRange)?.to === undefined) return;

    close();
  };

  const handleTimeChange = (event: ChangeEvent<HTMLInputElement>) => {
    const time = event.target.value;

    if (!dateValue) {
      setTimeValue(time);
      return;
    }

    const [hours, minutes] = time.split(':').map((str) => parseInt(str, 10));
    const newSelectedDate = setHours(setMinutes(dateValue as Date, minutes), hours);
    setDateValue(newSelectedDate);

    setTimeValue(time);
    validateTime(newSelectedDate);
  };

  const handleMonthChange = (month: Date) => {
    if (disablePastDays && differenceInMonths(month, today) < 0) return;

    setMonth(month);
  };

  const getStart = () => {
    if (startMonth === undefined) return undefined;

    return disablePastDays && startMonth < today ? today : startMonth;
  };

  const getEnd = () => {
    const start = getStart();

    if (endMonth === undefined || start === undefined) return undefined;

    return endMonth < start ? start : endMonth;
  };

  const close = () => {
    setVisible(false);
  };

  const toggleVisibility = () => {
    setDateValue(submitValue);
    setTimeValue(getTimeFromDate(submitValue as Date));
    setVisible((state) => !state);
  };

  useEffect(() => {
    setSubmitValue(value);
  }, [value, isRangeMode]);

  const datePicker = (
    <>
      <DayPicker
        mode={mode}
        required={isRangeMode}
        showOutsideDays
        locale={locale}
        today={today}
        month={month}
        disabled={
          {
            ...(disablePastDays && { before: today }),
            ...(disableFutureDays && { after: today }),
          } as Matcher
        }
        {...((startMonth || disablePastDays) && {
          startMonth: disablePastDays ? today : getStart(),
        })}
        {...((endMonth || disableFutureDays) && {
          endMonth: disableFutureDays ? today : getEnd(),
        })}
        captionLayout={captionLayout}
        id={id}
        min={min}
        numberOfMonths={numberOfMonths}
        selected={dateValue as any}
        onSelect={(selected: Date | DateRange | undefined) => handleSelect(selected)}
        onMonthChange={handleMonthChange}
        classNames={styles}
        {...datepickerProps}
      />

      {!isRangeMode && hasTime && (
        <div className={styles.datePickerTime}>
          <InputDescription
            className={styles.datePickerTimeSelect}
            label={t('datePicker.time.label')}
            errorMessage={timeError}
          >
            <TextInput type="time" value={timeValue} onChange={handleTimeChange} data-testid="date-picker-time" />
          </InputDescription>
        </div>
      )}

      {defaultIsSubmitNeeded && (
        <PrimaryButton
          data-testid="date-picker-submit"
          className={styles.datePickerSubmit}
          small
          disabled={dateValue === value || (hasTime && timeError !== undefined)}
          onClick={() => handleSelect(dateValue, true)}
        >
          {submitLabel || t('datePicker.submit')}
        </PrimaryButton>
      )}
    </>
  );

  if (asDropdown)
    return (
      <div
        ref={containerRef}
        className={classNames(
          styles.selectWrap,
          styles.wrapper,
          selectClassName,
          visible && styles.isOpen,
          value && styles.hasValue,
          disabled && styles.disabled
        )}
        data-testid="date-picker-container"
      >
        <button
          type="button"
          onClick={toggleVisibility}
          className={classNames(styles.datePickerButton, styles.select)}
          disabled={disabled}
          data-testid="date-picker-button"
          suppressHydrationWarning
        >
          {dropdownLabel}
        </button>
        {isClearable && value ? (
          <div className={classNames(styles.icon, styles.hasButton)}>
            <IconButton
              className={styles.iconButton}
              naked
              small
              disabled={disabled}
              aria-label={t('datePicker.clear')}
              onClick={() => handleSelect(null, true)}
            >
              <CloseIcon />
            </IconButton>
          </div>
        ) : showCalendarIcon ? (
          <div className={styles.icon}>
            <CalendarIcon />
          </div>
        ) : (
          <ChevronIcon className={styles.arrow} />
        )}

        <Dropdown
          referenceElement={containerRef.current}
          className={datepickerContainerClassName}
          isOpen={visible}
          handleClose={close}
          data-testid="date-picker-dropdown"
        >
          {datePicker}
        </Dropdown>
      </div>
    );

  return <div className={datepickerContainerClassName}>{datePicker}</div>;
}
